import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle,
  Copy,
  ExternalLink,
  Eye,
  Link2,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  X,
  Maximize2,
  Minimize2
} from 'lucide-react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useExpandable } from '../../hooks/useExpandable';
import Breadcrumbs from '../../components/Admin/Breadcrumbs';
import { useNotifications } from '../../hooks/useNotifications';
import { useConfirm } from '../../contexts/ConfirmContext';
import { buildReviewSummary, ResidenceResponses } from '../ResidenceAnalysis/residenceAnalysisFormHelpers';

interface ResidenceAnalysisItem {
  _id: string;
  token: string;
  status: string;
  expiresAt: string;
  clientLabel: string;
  clientEmailHint: string;
  adminNotes: string;
  formUrl: string;
  submittedAt?: string;
  approvedAt?: string;
  multiUse?: boolean;
  parentLinkId?: string | null;
  responses?: Record<string, unknown>;
  conclusion?: {
    viableBasis: string;
    pendingDocuments: string;
    viabilityLevel: string;
    recommendedStrategy: string;
    nextSteps: string;
  };
  createdAt: string;
}

const STATUS_LABELS: Record<string, string> = {
  awaiting_client: 'Aguardando cliente',
  submitted: 'Enviado',
  in_review: 'Em análise',
  approved: 'Aprovado',
  rejected: 'Rejeitado',
  expired: 'Expirado',
  revoked: 'Revogado'
};

const STATUS_COLORS: Record<string, string> = {
  awaiting_client: 'bg-gray-100 text-gray-800',
  submitted: 'bg-blue-100 text-blue-800',
  in_review: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  expired: 'bg-slate-100 text-slate-600',
  revoked: 'bg-red-50 text-red-600'
};

type ViabilityLevel = '' | 'alta' | 'media' | 'baixa';

type ConclusionForm = {
  viableBasis: string;
  pendingDocuments: string;
  viabilityLevel: ViabilityLevel;
  recommendedStrategy: string;
  nextSteps: string;
};

const emptyConclusion = (): ConclusionForm => ({
  viableBasis: '',
  pendingDocuments: '',
  viabilityLevel: '',
  recommendedStrategy: '',
  nextSteps: ''
});

function normalizeConclusion(raw?: ResidenceAnalysisItem['conclusion']): ConclusionForm {
  const level = raw?.viabilityLevel;
  const viabilityLevel: ViabilityLevel =
    level === 'alta' || level === 'media' || level === 'baixa' ? level : '';
  return {
    viableBasis: raw?.viableBasis ?? '',
    pendingDocuments: raw?.pendingDocuments ?? '',
    viabilityLevel,
    recommendedStrategy: raw?.recommendedStrategy ?? '',
    nextSteps: raw?.nextSteps ?? ''
  };
}

const AdminResidenceAnalysis: React.FC = () => {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const confirm = useConfirm();
  const [items, setItems] = useState<ResidenceAnalysisItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkForm, setLinkForm] = useState({
    clientLabel: '',
    clientEmailHint: '',
    expiresInDays: 30,
    adminNotes: '',
    multiUse: false
  });
  const [creatingLink, setCreatingLink] = useState(false);
  const [lastCreatedUrl, setLastCreatedUrl] = useState('');

  const [selected, setSelected] = useState<ResidenceAnalysisItem | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const { isExpanded: isDetailExpanded, setIsExpanded: setIsDetailExpanded, toggleExpanded: toggleDetailExpanded } = useExpandable();
  useEscapeKey(() => { setSelected(null); setIsDetailExpanded(false); }, !!selected || detailLoading);
  const [editResponses, setEditResponses] = useState<Record<string, unknown>>({});
  const [conclusion, setConclusion] = useState<ConclusionForm>(emptyConclusion());
  const [adminNotes, setAdminNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/residence-analysis', {
        params: { page, limit: 15, status: statusFilter, search }
      });
      setItems(res.data.items);
      setTotalPages(res.data.totalPages);
    } catch {
      notifications.error.network();
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search]);

  useEffect(() => {
    fetchList();
  }, [fetchList]); // eslint-disable-line react-hooks/exhaustive-deps

  const createLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatingLink(true);
    try {
      const res = await axios.post('/residence-analysis/links', linkForm);
      setLastCreatedUrl(res.data.formUrl);
      notifications.showSuccess('Link gerado', 'Compartilhe o link com o cliente.');
      setShowLinkForm(false);
      setLinkForm({ clientLabel: '', clientEmailHint: '', expiresInDays: 30, adminNotes: '', multiUse: false });
      fetchList();
    } catch (err: any) {
      notifications.showError('Erro', err.response?.data?.message || 'Erro ao gerar link');
    } finally {
      setCreatingLink(false);
    }
  };

  const openDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`/residence-analysis/${id}`);
      const item = res.data as ResidenceAnalysisItem;
      setSelected(item);
      setEditResponses((item.responses as Record<string, unknown>) || {});
      setConclusion(normalizeConclusion(item.conclusion));
      setAdminNotes(item.adminNotes || '');
    } catch {
      notifications.error.network();
    } finally {
      setDetailLoading(false);
    }
  };

  const saveDetail = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.put(`/residence-analysis/${selected._id}`, {
        responses: editResponses,
        conclusion,
        adminNotes,
        status: selected.status === 'submitted' ? 'in_review' : selected.status
      });
      notifications.showSuccess('Salvo', 'Registro atualizado.');
      await openDetail(selected._id);
      fetchList();
    } catch {
      notifications.showError('Erro', 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    if (!selected) return;
    if (!conclusion.viableBasis.trim()) {
      notifications.showWarning('Conclusão', 'Preencha o fundamento/via legal mais viável.');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`/residence-analysis/${selected._id}/approve`, { conclusion });
      notifications.showSuccess('Aprovado', 'Análise marcada como aprovada.');
      setSelected(null);
      setIsDetailExpanded(false);
      fetchList();
    } catch {
      notifications.showError('Erro', 'Erro ao aprovar');
    } finally {
      setSaving(false);
    }
  };

  const revokeLink = async (id: string) => {
    if (!(await confirm({ title: 'Revogar link', message: 'O cliente não poderá mais preencher o formulário.', confirmText: 'Revogar', variant: 'danger' }))) return;
    try {
      await axios.post(`/residence-analysis/${id}/revoke`);
      fetchList();
      if (selected?._id === id) { setSelected(null); setIsDetailExpanded(false); }
    } catch {
      notifications.showError('Erro', 'Erro ao revogar');
    }
  };

  const deleteItem = async (id: string) => {
    if (!(await confirm({ title: 'Excluir registro', message: 'Este registro será excluído permanentemente.', confirmText: 'Excluir', variant: 'danger' }))) return;
    try {
      await axios.delete(`/residence-analysis/${id}`);
      fetchList();
      if (selected?._id === id) setSelected(null);
    } catch {
      notifications.showError('Erro', 'Erro ao excluir');
    }
  };

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    notifications.showSuccess('Copiado', 'Link copiado para a área de transferência.');
  };

  const clientName = (item: ResidenceAnalysisItem) =>
    (item.responses as any)?.personal?.fullName ||
    item.clientLabel ||
    item.clientEmailHint ||
    '—';

  const renderResponseSummary = () => {
    const r = editResponses as Partial<ResidenceResponses>;
    if (!r?.personal?.fullName) {
      return <p className="text-gray-500 text-sm">Sem respostas ainda.</p>;
    }
    const sections = buildReviewSummary(r as ResidenceResponses, t);
    return (
      <div className="space-y-4 text-sm max-h-96 overflow-y-auto">
        {sections.map((section) => (
          <section key={section.title}>
            <h4 className="font-semibold text-gray-800 mb-1">{section.title}</h4>
            <dl className="space-y-1">
              {section.lines.map((row) => (
                <div key={row.label}>
                  <span className="text-gray-500">{row.label}: </span>
                  <span className="text-gray-900">{row.value}</span>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('admin.residenceAnalysis')}</h1>
            <p className="text-gray-600 text-sm mt-1">{t('admin.residenceAnalysisDesc')}</p>
          </div>
          <button
            type="button"
            onClick={() => setShowLinkForm(!showLinkForm)}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {t('admin.residenceAnalysisNewLink')}
          </button>
        </div>

        {lastCreatedUrl && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex flex-wrap items-center gap-3">
            <Link2 className="w-5 h-5 text-green-700 shrink-0" />
            <code className="text-sm text-green-900 break-all flex-1">{lastCreatedUrl}</code>
            <button
              type="button"
              onClick={() => copyUrl(lastCreatedUrl)}
              className="p-2 text-green-800 hover:bg-green-100 rounded-lg transition-colors duration-200"
              title="Copiar link"
            >
              <Copy className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => setLastCreatedUrl('')}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              title="Fechar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {showLinkForm && (
          <form onSubmit={createLink} className="mb-6 bg-white rounded-xl shadow p-6 space-y-4">
            <h2 className="font-semibold text-gray-900">{t('admin.residenceAnalysisGenerate')}</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <label className="block text-sm">
                <span className="text-gray-700">Nome / referência do cliente</span>
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={linkForm.clientLabel}
                  onChange={(e) => setLinkForm((f) => ({ ...f, clientLabel: e.target.value }))}
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-700">E-mail do cliente (opcional)</span>
                <input
                  type="email"
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={linkForm.clientEmailHint}
                  onChange={(e) => setLinkForm((f) => ({ ...f, clientEmailHint: e.target.value }))}
                />
              </label>
              <label className="block text-sm">
                <span className="text-gray-700">Validade (dias)</span>
                <input
                  type="number"
                  min={1}
                  max={90}
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={linkForm.expiresInDays}
                  onChange={(e) => setLinkForm((f) => ({ ...f, expiresInDays: Number(e.target.value) }))}
                />
              </label>
              <label className="block text-sm md:col-span-2">
                <span className="text-gray-700">Notas internas</span>
                <textarea
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  rows={2}
                  value={linkForm.adminNotes}
                  onChange={(e) => setLinkForm((f) => ({ ...f, adminNotes: e.target.value }))}
                />
              </label>
              <label className="flex items-center gap-2 text-sm md:col-span-2">
                <input
                  type="checkbox"
                  checked={linkForm.multiUse}
                  onChange={(e) => setLinkForm((f) => ({ ...f, multiUse: e.target.checked }))}
                />
                <span className="text-gray-700">{t('admin.residenceAnalysisMultiUse')}</span>
              </label>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={creatingLink}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creatingLink ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    <span>Gerando...</span>
                  </>
                ) : (
                  <span>Gerar link</span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setShowLinkForm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200"
              >
                Cancelar
              </button>
            </div>
          </form>
        )}

        <div className="bg-white rounded-xl shadow mb-4 p-4 flex flex-wrap gap-3">
          <input
            type="search"
            placeholder={t('common.search')}
            className="border rounded-lg px-3 py-2 flex-1 min-w-[200px]"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
          <select
            className="border rounded-lg px-3 py-2"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Todos os status</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={fetchList}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
            title="Atualizar"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        <div className="bg-white rounded-xl shadow overflow-hidden">
          {loading ? (
            <div className="p-12 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : items.length === 0 ? (
            <p className="p-8 text-center text-gray-500">{t('admin.residenceAnalysisEmpty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Cliente</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Enviado</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-600">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((item) => (
                    <tr key={item._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{clientName(item)}</span>
                          {item.multiUse && (
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              {t('admin.residenceAnalysisMultiUseBadge')}
                            </span>
                          )}
                        </div>
                        {item.clientLabel && (
                          <div className="text-xs text-gray-500">{item.clientLabel}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                            STATUS_COLORS[item.status] || 'bg-gray-100'
                          }`}
                        >
                          {STATUS_LABELS[item.status] || item.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        {item.submittedAt
                          ? new Date(item.submittedAt).toLocaleString('pt-PT')
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-right space-x-1">
                        <button
                          type="button"
                          onClick={() => copyUrl(item.formUrl)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                          title="Copiar link"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => openDetail(item._id)}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors duration-200"
                          title="Ver / editar"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {item.status === 'awaiting_client' && (
                          <button
                            type="button"
                            onClick={() => revokeLink(item._id)}
                            className="p-2 text-amber-600 hover:bg-amber-100 rounded-lg transition-colors duration-200"
                            title="Revogar link"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => deleteItem(item._id)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors duration-200"
                          title="Excluir"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 p-4 border-t">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <p className="text-sm text-gray-700">
                Página <span className="font-medium">{page}</span> de{' '}
                <span className="font-medium">{totalPages}</span>
              </p>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próxima
              </button>
            </div>
          )}
        </div>
      </div>

      {(selected || detailLoading) && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4">
          <div className={`bg-white rounded-xl shadow-xl w-full overflow-y-auto ${
            isDetailExpanded ? 'max-w-[95vw] max-h-[95vh]' : 'max-w-3xl max-h-[90vh]'
          }`}>
            {detailLoading && !selected ? (
              <div className="p-12 flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
              </div>
            ) : selected ? (
              <>
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-start">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">{clientName(selected)}</h2>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        STATUS_COLORS[selected.status]
                      }`}
                    >
                      {STATUS_LABELS[selected.status]}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={toggleDetailExpanded}
                      className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                      title={isDetailExpanded ? 'Restaurar' : 'Expandir'}
                    >
                      {isDetailExpanded ? <Minimize2 className="w-6 h-6" /> : <Maximize2 className="w-6 h-6" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setSelected(null); setIsDetailExpanded(false); }}
                      className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                      title="Fechar"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => copyUrl(selected.formUrl)}
                      className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 transition-colors duration-200"
                    >
                      <Copy className="w-4 h-4" /> Copiar link do formulário
                    </button>
                    <a
                      href={selected.formUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800 transition-colors duration-200"
                    >
                      <ExternalLink className="w-4 h-4" /> Abrir formulário
                    </a>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Respostas do cliente</h3>
                    {renderResponseSummary()}
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">
                      Conclusão da consulta (administrador)
                    </h3>
                    <div className="space-y-3">
                      <label className="block text-sm">
                        Fundamento/via legal mais viável
                        <input
                          className="mt-1 w-full border rounded-lg px-3 py-2"
                          value={conclusion.viableBasis}
                          onChange={(e) =>
                            setConclusion((c) => ({ ...c, viableBasis: e.target.value }))
                          }
                        />
                      </label>
                      <label className="block text-sm">
                        Documentos pendentes
                        <textarea
                          className="mt-1 w-full border rounded-lg px-3 py-2"
                          rows={2}
                          value={conclusion.pendingDocuments}
                          onChange={(e) =>
                            setConclusion((c) => ({ ...c, pendingDocuments: e.target.value }))
                          }
                        />
                      </label>
                      <fieldset>
                        <legend className="text-sm font-medium text-gray-700">Grau de viabilidade</legend>
                        <div className="flex gap-4 mt-1">
                          {(['alta', 'media', 'baixa'] as const).map((v) => (
                            <label key={v} className="inline-flex items-center gap-2 text-sm">
                              <input
                                type="radio"
                                checked={conclusion.viabilityLevel === v}
                                onChange={() => setConclusion((c) => ({ ...c, viabilityLevel: v }))}
                              />
                              {v.charAt(0).toUpperCase() + v.slice(1)}
                            </label>
                          ))}
                        </div>
                      </fieldset>
                      <label className="block text-sm">
                        Estratégia recomendada
                        <textarea
                          className="mt-1 w-full border rounded-lg px-3 py-2"
                          rows={2}
                          value={conclusion.recommendedStrategy}
                          onChange={(e) =>
                            setConclusion((c) => ({ ...c, recommendedStrategy: e.target.value }))
                          }
                        />
                      </label>
                      <label className="block text-sm">
                        Próximos passos
                        <textarea
                          className="mt-1 w-full border rounded-lg px-3 py-2"
                          rows={2}
                          value={conclusion.nextSteps}
                          onChange={(e) =>
                            setConclusion((c) => ({ ...c, nextSteps: e.target.value }))
                          }
                        />
                      </label>
                      <label className="block text-sm">
                        Notas internas
                        <textarea
                          className="mt-1 w-full border rounded-lg px-3 py-2"
                          rows={2}
                          value={adminNotes}
                          onChange={(e) => setAdminNotes(e.target.value)}
                        />
                      </label>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-2 border-t">
                    <button
                      type="button"
                      onClick={saveDetail}
                      disabled={saving}
                      className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          <span>Salvando...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          <span>Salvar</span>
                        </>
                      )}
                    </button>
                    {selected.status !== 'approved' && (
                      <button
                        type="button"
                        onClick={approve}
                        disabled={saving}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <CheckCircle className="w-4 h-4" />
                        <span>Aprovar análise</span>
                      </button>
                    )}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminResidenceAnalysis;
